import {
  findContact,
  findContactBySession,
  listContacts,
  contactDisplayName,
  onContactsChanged,
} from '../storage/pairingStore.ts'
import {
  persistContactsForServiceWorker,
  notifyLabelFromContact,
  lookupContactNameForHints,
} from '../storage/contactsDbForSw.ts'

const CHANNEL = 'zion_active_chat'

let channel: BroadcastChannel | null = null
let lastPeerIds: string[] = []
let hooksInstalled = false

function getChannel(): BroadcastChannel | null {
  if (channel) return channel
  try {
    channel = new BroadcastChannel(CHANNEL)
    return channel
  } catch {
    return null
  }
}

function postToServiceWorker(payload: Record<string, unknown>): void {
  try {
    navigator.serviceWorker?.controller?.postMessage(payload)
  } catch {
    /* ignore */
  }
}

function isHexSessionId(value: string): boolean {
  const s = value.trim()
  if (!s) return true
  return /^[a-f0-9-]{8,}$/i.test(s)
}

/** Persiste contatti in IndexedDB (SW) + mappa in memoria SW. */
export async function syncContactNamesToSw(): Promise<void> {
  try {
    const contacts = await listContacts()
    const names = await persistContactsForServiceWorker(contacts)
    try {
      const { persistSessionForServiceWorker } = await import('../storage/sessionDbForSw.ts')
      const snap = await persistSessionForServiceWorker(contacts)
      if (snap) {
        const msgSnap = { type: 'SET_SESSION_SNAPSHOT', snapshot: snap }
        postToServiceWorker(msgSnap)
        try {
          getChannel()?.postMessage(msgSnap)
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* optional */
    }
    // Anche mappa in-memory (veloce se SW già attivo)
    for (const c of contacts) {
      const display = (notifyLabelFromContact(c) || contactDisplayName(c)).trim()
      if (!display || isHexSessionId(display)) continue
      for (const raw of [
        c.contactId,
        c.sessionToken,
        c.nickname,
        c.customName,
        c.inboundQueueId,
      ]) {
        const k = String(raw || '')
          .trim()
          .toLowerCase()
        if (!k) continue
        names[k] = display
      }
    }
    const msg = { type: 'SET_CONTACT_NAMES', names }
    postToServiceWorker(msg)
    try {
      getChannel()?.postMessage(msg)
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/** Comunica al SW la chat aperta (contactId + sessionToken) e se la UI è visibile. */
export function publishActiveChat(peerIds: string[] | null, visible = true): void {
  const ids = (peerIds || [])
    .map((x) => String(x || '').trim().toLowerCase())
    .filter(Boolean)
  lastPeerIds = ids

  const msg = {
    type: 'SET_ACTIVE_CHAT',
    peerId: ids[0] || null,
    peerIds: ids,
    visible: visible && ids.length > 0,
  }

  try {
    getChannel()?.postMessage(msg)
  } catch {
    /* ignore */
  }
  postToServiceWorker(msg)
}

export async function syncActiveChatToSw(contactId: string): Promise<void> {
  const id = contactId.trim()
  if (!id) {
    publishActiveChat(null, false)
    return
  }
  const contact = await findContact(id)
  const ids = [id]
  if (contact?.sessionToken) ids.push(contact.sessionToken)
  if (contact?.nickname) {
    ids.push(contact.nickname)
    ids.push(`@${contact.nickname}`)
  }
  const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'
  publishActiveChat(ids, visible)
  void syncContactNamesToSw()
}

export function clearActiveChatOnSw(): void {
  publishActiveChat(null, false)
}

/** True se la chat con questo peer è aperta e l'app è visibile (per sopprimere notifiche duplicate). */
export function isActiveChatPeerVisible(peerId: string): boolean {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return false
  return peerMatchesActive(peerId, [peerId])
}

function peerMatchesActive(peerId: string, peerIds: string[]): boolean {
  const candidates = [peerId, ...peerIds]
    .map((x) => String(x || '').trim().toLowerCase())
    .filter(Boolean)
  if (!candidates.length || !lastPeerIds.length) return false
  return candidates.some((c) => lastPeerIds.includes(c))
}

/** Risponde al SW: GET_CONTACT_NAME via MessageChannel (customName locale). */
function onSwGetContactName(event: MessageEvent): void {
  const msg = event.data
  if (!msg || msg.type !== 'GET_CONTACT_NAME') return
  void (async () => {
    const peerId = String(msg.peerId || '').trim()
    let name = peerId ? await lookupContactNameForHints([peerId]) : null
    if (!name && peerId) {
      const contact = await findContactBySession(peerId)
      if (contact) name = notifyLabelFromContact(contact)
    }
    try {
      event.ports?.[0]?.postMessage({ name: name || '' })
    } catch {
      /* ignore */
    }
  })()
}

/** Risponde al SW: CHECK_ACTIVE_CHAT via MessageChannel. */
function onSwCheckActiveChat(event: MessageEvent): void {
  const msg = event.data
  if (!msg || msg.type !== 'CHECK_ACTIVE_CHAT') return
  const peerId = String(msg.peerId || '')
  const peerIds = Array.isArray(msg.peerIds) ? msg.peerIds.map(String) : []
  const visible = document.visibilityState === 'visible'
  const active = visible && peerMatchesActive(peerId, peerIds)
  try {
    event.ports?.[0]?.postMessage({ active, peerId, visible })
  } catch {
    /* ignore */
  }
}

/** Aggiorna visible su focus/blur senza cambiare peer. */
export function initActiveChatVisibilityHooks(): void {
  if (hooksInstalled || typeof document === 'undefined') return
  hooksInstalled = true

  onContactsChanged(() => {
    void syncContactNamesToSw()
  })

  const flush = (): void => {
    const visible = document.visibilityState === 'visible'
    if (!lastPeerIds.length) {
      publishActiveChat(null, false)
      return
    }
    publishActiveChat(lastPeerIds, visible)
  }

  const flushNamesBeforeBackground = (): void => {
    void syncContactNamesToSw()
  }

  document.addEventListener('visibilitychange', () => {
    flush()
    if (document.visibilityState === 'hidden') flushNamesBeforeBackground()
  })
  window.addEventListener('focus', flush)
  window.addEventListener('blur', () => {
    if (lastPeerIds.length) publishActiveChat(lastPeerIds, false)
    flushNamesBeforeBackground()
  })
  window.addEventListener('pagehide', () => {
    publishActiveChat(null, false)
    flushNamesBeforeBackground()
  })

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      onSwCheckActiveChat(event)
      onSwGetContactName(event)
    })
    navigator.serviceWorker.ready.then(() => {
      void syncContactNamesToSw()
    })
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      void syncContactNamesToSw()
    })
  }

  // Refresh periodico leggero (contatti rinominati)
  window.setInterval(() => {
    void syncContactNamesToSw()
  }, 60_000)
}
